import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/Appcontext";
import "../../../assets/css/admin/adminAuth.css";

const validateEmail = (email) => {
  if (!email) return "Email is required";
  return null;
};
const validatePassword = (password) => {
  if (!password) return "Password is required";
  return null;
};

const useAdminLogin = () => {
  const navigate = useNavigate();
  const { baseUrl } = useContext(AppContext);
  const [formState, setFormState] = useState({
    email: "",
    password: "",
    rememberMe: false,
    emailError: null,
    passwordError: null,
    formError: null,
    isLoading: false,
    touched: { email: false, password: false },
  });
  const updateField = (field, value) =>
    setFormState((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Error`]: null,
      formError: null,
    }));
  const markFieldTouched = (field) =>
    setFormState((prev) => ({
      ...prev,
      touched: { ...prev.touched, [field]: true },
    }));
  const validateForm = () => {
    const emailError = validateEmail(formState.email);
    const passwordError = validatePassword(formState.password);
    setFormState((prev) => ({
      ...prev,
      emailError,
      passwordError,
      touched: { email: true, password: true },
    }));
    return !emailError && !passwordError;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormState((prev) => ({ ...prev, isLoading: true, formError: null }));
    const loginFormData = new FormData();
    loginFormData.append("email", formState.email);
    loginFormData.append("password", formState.password);
    try {
      const response = await axios.post(
        `${baseUrl}/admin/login`,
        loginFormData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setFormState((prev) => ({ ...prev, isLoading: false }));

      const firstname =
        response.data.admin?.firstname ||
        response.data.admin?.email?.split("@")[0] ||
        "Admin";

      localStorage.setItem("email", formState.email);
      localStorage.setItem("password", formState.password);
      localStorage.setItem("firstname", firstname);
      localStorage.setItem("lastname", response.data.user?.lastname || "");
      localStorage.setItem("role", "admin");
      localStorage.setItem("token", response.data.token || "");

      Swal.fire({
        icon: "success",
        title: `Welcome, ${firstname}!`,
        text: "Admin login successful",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate("/admin/dashboard"));
    } catch (error) {
      let errorMessage = "Login failed. Please try again.";
      if (error.response?.data?.errors)
        errorMessage = Object.values(error.response.data.errors)
          .flat()
          .join(", ");
      else if (error.response?.data?.message)
        errorMessage = error.response.data.message;
      else if (!error.response)
        errorMessage = "Network error. Please check your connection.";
      setFormState((prev) => ({
        ...prev,
        formError: errorMessage,
        isLoading: false,
      }));
    }
  };
  return { formState, updateField, markFieldTouched, handleSubmit };
};

const AdminLogin = () => {
  const { formState, updateField, markFieldTouched, handleSubmit } =
    useAdminLogin();
  const {
    email,
    password,
    rememberMe,
    emailError,
    passwordError,
    formError,
    isLoading,
    touched,
  } = formState;
  const [showPassword, setShowPassword] = useState(false);
  const showEmailError = touched.email && emailError;
  const showPasswordError = touched.password && passwordError;

  return (
    <div className="admin-auth-page">
      <div className="admin-auth-gradient-circle admin-auth-circle-1"></div>
      <div className="admin-auth-gradient-circle admin-auth-circle-2"></div>
      <div className="admin-auth-gradient-circle admin-auth-circle-3"></div>
      <div className="admin-auth-gradient-circle admin-auth-circle-4"></div>
      <div className="admin-auth-container">
        <div className="admin-auth-card">
          <div className="admin-auth-avatar-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          {formError && (
            <div className="admin-auth-alert admin-auth-alert-error">
              {formError}
            </div>
          )}
          <div className="admin-auth-input-group">
            <div className="admin-auth-input-wrapper">
              <div className="admin-auth-input-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <input
                type="email"
                id="email"
                className={`admin-auth-input-field ${showEmailError ? "admin-auth-input-error" : ""}`}
                placeholder="Email ID"
                value={email}
                onChange={(e) => updateField("email", e.target.value)}
                onBlur={() => markFieldTouched("email")}
                disabled={isLoading}
              />
            </div>
            {showEmailError && (
              <div className="admin-auth-error-message">{emailError}</div>
            )}
          </div>
          <div className="admin-auth-input-group">
            <div className="admin-auth-input-wrapper">
              <div className="admin-auth-input-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className={`admin-auth-input-field ${showPasswordError ? "admin-auth-input-error" : ""}`}
                placeholder="Password"
                value={password}
                onChange={(e) => updateField("password", e.target.value)}
                onBlur={() => markFieldTouched("password")}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}
                disabled={isLoading}
              />
              <button
                type="button"
                className="admin-auth-eye-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="18"
                    height="18"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="18"
                    height="18"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {showPasswordError && (
              <div className="admin-auth-error-message">{passwordError}</div>
            )}
          </div>
          <div className="admin-auth-form-options">
            <label className="admin-auth-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => updateField("rememberMe", e.target.checked)}
                disabled={isLoading}
              />
              <span className="admin-auth-checkbox-text">Remember me</span>
            </label>
            <a href="#forgot" className="admin-auth-forgot-link">
              Forgot Password?
            </a>
          </div>
          <button
            type="button"
            className="admin-auth-login-button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "LOGIN"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
